import { Avatar } from "@admin/components/atoms/Avatar";
import { Button } from "@admin/components/atoms/Button";
import { Input } from "@admin/components/atoms/Input";
import { Modal, ModalProps } from "@admin/components/atoms/Modal";
import {
  deleteShift,
  LocationUser,
  Shift,
  updateShift,
} from "@enos-monorepo/lib";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";

interface EditOpenShiftModalProps extends ModalProps {
  personnelList?: LocationUser[];
  shift?: Shift;
  companyId: string;
}

export function EditOpenShiftModal({
  personnelList = [],
  shift,
  companyId,
  ...props
}: EditOpenShiftModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedPersonel, setSelectedPersonel] = useState<LocationUser>();
  const [beginningTime, setBeginningTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [calculatedTime, setCalculatedTime] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const deleteMutation = useMutation({
    mutationFn: deleteShift,
    onSuccess: () => {
      toast.success(t("shift.The shift has been edited"));
      queryClient.invalidateQueries({ queryKey: ["shifts"] });
      props.onClose?.();
    },
    onError: () => {
      toast.error(t("shift.Unable to edit the shift"));
    },
  });
  const updateMutation = useMutation({
    mutationFn: updateShift,
    onSuccess: () => {
      toast.success(t("shift.The shift has been edited"));
      queryClient.invalidateQueries({ queryKey: ["shifts"] });
      props.onClose?.();
    },
    onError: () => {
      toast.error(t("shift.Unable to edit the shift"));
    },
  });

  const handleUpdateShift = () => {
    if (!shift || !shift.id || !selectedPersonel) return;
    updateMutation.mutate({
      id: shift.id,
      userId: selectedPersonel?.userId,
      companyId: companyId,
      companyLocationId: selectedPersonel?.companyLocationId,
      plannedStartDate: `${shift?.plannedStartDate?.split("T")[0]}T${beginningTime}`,
      plannedEndDate: `${shift?.plannedEndDate?.split("T")[0]}T${endTime}`,
    });
  };

  useEffect(() => {
    if (shift && shift.plannedStartDate && shift.plannedEndDate) {
      setBeginningTime(extractTime(shift.plannedStartDate));
      setEndTime(extractTime(shift.plannedEndDate));
    }
  }, [shift]);

  useEffect(() => {
    if (beginningTime && endTime) {
      const diffTime = calculateTimeDifference(beginningTime, endTime);
      setCalculatedTime(diffTime);
    } else {
      setCalculatedTime("");
    }
  }, [beginningTime, endTime]);

  return (
    <Modal
      {...props}
      title={`${t("shift.Edit Shift")} / ${formatDate(shift?.plannedStartDate ?? "")}`}
      actionButtons={
        <>
          <Button
            variant="secondary"
            onClick={props?.onClose}
            disabled={updateMutation.isPending || deleteMutation.isPending}
          >
            {t("common.Discard")}
          </Button>
          <Button
            property="danger"
            onClick={() => {
              if (!shift?.id) return;
              deleteMutation.mutate(shift.id);
            }}
            disabled={deleteMutation.isPending || updateMutation.isPending}
            loading={deleteMutation.isPending}
          >
            {t("shift.Delete Shift")}
          </Button>
          <Button
            variant="primary"
            onClick={handleUpdateShift}
            disabled={updateMutation.isPending || deleteMutation.isPending}
            loading={updateMutation.isPending}
          >
            {t("common.Save Changes")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col w-100 gap-5">
        <div className="flex items-center flex-row lg:flex-nowrap gap-2.5">
          <label className="form-label flex items-center gap-1 max-w-56 !font-normal !text-gray-800">
            {t("shift.Change Personnel")}
          </label>
          <div className="flex flex-col w-full rounded-md border border-[#dbdfe9] relative">
            <div
              className="flex flex-row items-center w-full cursor-pointer"
              onClick={() => setIsDropdownOpen((prev) => !prev)}
            >
              {selectedPersonel ? (
                <>
                  <div className="w-[48px] h-[48px] p-2 gap-2.5 !pr-0 mr-2.25">
                    <Avatar
                      src={selectedPersonel?.userPhoto}
                      alt={`${selectedPersonel?.firstName} ${selectedPersonel?.lastName}`}
                      size="sm"
                    />
                  </div>
                  <div className="flex flex-col pl-1 py-1 h-[48px] w-full">
                    <p className="text-[14px] font-medium text-[#252f4a] leading-3.5">
                      {selectedPersonel?.firstName} {selectedPersonel?.lastName}
                    </p>
                    <p className="text-[11px] text-[#78829D] font-normal leading-3">
                      @{selectedPersonel?.firstName}
                    </p>
                  </div>
                </>
              ) : (
                <div className="flex flex-row items-center h-[38px] pl-3 py-1 text-sm w-full">
                  {t("shift.Select a Personnel to Assign this Shift")}
                </div>
              )}
              <div className="w-[46px] h-full px-3.5 g-2.5">
                <div className="text-gray-500">
                  <svg
                    className="w-4 h-4 transform transition-transform"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </div>
            {isDropdownOpen && (
              <div className="absolute top-full left-0 w-full bg-white border border-gray-300 z-10 max-h-64 overflow-y-auto rounded-md">
                {personnelList.map((person) => (
                  <div
                    key={person.userId}
                    onClick={() => {
                      setSelectedPersonel(person);
                      setIsDropdownOpen(false);
                    }}
                    className="flex items-center p-2 cursor-pointer hover:bg-gray-100"
                  >
                    <Avatar
                      size="sm"
                      src={person.userPhoto}
                      alt={`${person.firstName} ${person.lastName}`}
                      className="mr-2"
                    />
                    <div>
                      <p className="text-[14px] font-medium text-[#252f4a] leading-3.5">
                        {person.firstName} {person.lastName}
                      </p>
                      <p className="text-[11px] text-[#78829D] font-normal leading-3">
                        @{person.userName}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-baseline flex-row lg:flex-nowrap gap-2.5">
          <label className="form-label flex items-center gap-1 max-w-56 !font-normal !text-gray-800">
            {t("shift.Shift Beginning and End")}
          </label>
          <div className="flex flex-row w-full gap-2.5">
            <Input
              name="beginningTime"
              placeholder={t("shift.Beginning")}
              className="w-[50%]"
              type="time"
              value={beginningTime}
              onChange={(e) => setBeginningTime(e.target.value)}
            />
            <Input
              type="time"
              name="endTime"
              placeholder={t("shift.End")}
              className="w-[50%]"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
            />
          </div>
        </div>

        <div className="flex items-baseline flex-row lg:flex-nowrap gap-2.5">
          <label className="form-label flex items-center gap-1 max-w-56 !font-normal !text-gray-800">
            {t("shift.Calculated Time")}
          </label>
          <div className="flex flex-col w-full">
            <Input
              placeholder={calculatedTime || t("shift.Calculated Time")}
              readOnly
              disabled
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}

function formatDate(dateString: string) {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function extractTime(dateTimeString: string) {
  return dateTimeString.split("T")[1].slice(0, 5);
}

function calculateTimeDifference(start: string, end: string) {
  const [startHour, startMinute] = start.split(":").map(Number);
  const [endHour, endMinute] = end.split(":").map(Number);

  const startDate = new Date();
  const endDate = new Date();

  startDate.setHours(startHour, startMinute, 0, 0);
  endDate.setHours(endHour, endMinute, 0, 0);

  if (endDate < startDate) {
    endDate.setDate(endDate.getDate() + 1);
  }

  const diffMs = endDate.getTime() - startDate.getTime();
  const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  return `${diffHrs.toString().padStart(2, "0")}:${diffMins
    .toString()
    .padStart(2, "0")}`;
}
